import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RecruitingAgencyModal from './components/RecruitingAgencyModal';
import Img from '../../../Img';
import ProspectSectionRow from '../components/ProspectSectionRow';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import { PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.recruiting-agency';
const RECRUITING_STATUS_ENDPOINT = '/v1/cpp/recruiting-status';
const RECRUITING_AGENCY_ENDPOINT = '/v1/cpp/recruiting-agency';

const CollegeRecruitingAgency = () => {
    const profileData = useSelector((state) => state.profile?.data) || {};
    const authUser = useSelector((state) => state.auth?.user);
    const isOwnProfile = !!authUser && !!profileData.memberId && authUser.id === profileData.memberId;
    const dispatch = useDispatch();
    const { hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const isLocked = !hasAnnualPower;

    const [agency, setAgency] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (!isOwnProfile || !hasAnnualPower) {
            return;
        }
        const fetchAgency = async () => {
            try {
                const response = await getApiAxiosInstance().get(RECRUITING_STATUS_ENDPOINT);
                const rawAgency = response.data?.recruitingAgency;
                if (rawAgency?.agencyId) {
                    setAgency({ id: rawAgency.agencyId, name: rawAgency.agencyName });
                } else {
                    setAgency(null);
                }
            } catch (err) {
                dispatch(showErrorPopup({
                    message: 'We couldn\'t load your recruiting agency. Please try again.',
                    error: err,
                }));
                setAgency(null);
            }
        };
        fetchAgency();
    }, [isOwnProfile, hasAnnualPower, dispatch]);

    const handleEditClick = () => {
        if (isLocked) {
            openUpgradePopup(PowerFunnelGatedFeatures.CPP_RECRUITING_AGENCY_LOCKED);
            return;
        }
        setIsEditing(true);
    };

    const handleLockKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEditClick();
        }
    };

    const handleSave = async (selectedAgency) => {
        try {
            await getApiAxiosInstance().put(RECRUITING_AGENCY_ENDPOINT, {
                agencyId: selectedAgency?.id ?? null,
            });
            setAgency(selectedAgency);
            setIsEditing(false);
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t save your recruiting agency. Please try again.',
                error: err,
            }));
        }
    };

    return (
        <div
            className={styles.academicsSection}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div className={styles.academicsHeader}>
                <div className={styles.academicsTitleBlock}>
                    <div
                        className={styles.academicsTitle}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        College Recruiting Agency
                    </div>
                    <div
                        className={styles.academicsSubtitle}
                        data-testid={`${TESTID_PREFIX}.subtitle`}
                    >
                        Viewable by college coaches.
                    </div>
                </div>
                {isOwnProfile && (
                    <button
                        type="button"
                        className={styles.academicsEditBtn}
                        onClick={handleEditClick}
                        data-testid={`${TESTID_PREFIX}.edit-btn`}
                    >
                        <Img
                            src="/img/v4/pen.svg"
                            alt=""
                            className={styles.academicsEditIcon}
                        />
                        <span data-testid={`${TESTID_PREFIX}.edit-btn.text`}>Edit</span>
                    </button>
                )}
            </div>

            <div className={styles.academicsCard}>
                <div className={styles.academicsBody}>
                    {isLocked && (
                        <div
                            className={styles.sectionLockRow}
                            role="button"
                            tabIndex={0}
                            onClick={handleEditClick}
                            onKeyDown={handleLockKeyDown}
                            data-testid={`${TESTID_PREFIX}.lock`}
                        >
                            <span className={styles.socialLockBox}>
                                <Img
                                    src="/img/v4/lock.svg"
                                    alt="Locked"
                                    className={styles.socialLockIcon}
                                />
                            </span>
                        </div>
                    )}
                    {!isLocked && agency && (
                        <ProspectSectionRow
                            label="Agency"
                            value={agency.name}
                            testIdPrefix={TESTID_PREFIX}
                            testId="agency-name"
                        />
                    )}
                    {!isLocked && !agency && (
                        <div
                            className={styles.recruitingSectionEmptyText}
                            data-testid={`${TESTID_PREFIX}.empty-text`}
                        >
                            Click &ldquo;Edit&rdquo; to select a recruiting agency.
                        </div>
                    )}
                </div>
            </div>

            {isEditing && isOwnProfile && hasAnnualPower && (
                <RecruitingAgencyModal
                    currentAgency={agency}
                    onClose={() => setIsEditing(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default CollegeRecruitingAgency;
